import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { getSymbolFromCurrency } from '~helpers/currency';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Row, Col, Select, Checkbox, Input, InputNumber, Tooltip } from 'antd';
import {
  billingCycleOptions,
  cancellationPeriodTypeValue,
} from '~constants/property-field-options';
import { getBillingCycleText } from '~helpers/property-field-option';
import ReactQuill from '~components/react-quill';
import PropertyDetailWrapper from '~components/property-detail-wrapper';
import enhanceForm from '~hocs/enhance-form';
import * as queries from '~settings/pms-queries';
import { listingManagementFetch } from '~helpers/graphql';
import { getHtmlLength, htmlMinify } from '~helpers/property-edit';
import {
  propertyStatus,
  propertyTenancyAgreementUnit,
  propertyTenancyAgreementField,
  propertyTenancyAgreementTimeType,
  propertyTenancyAgreementCalculateType,
} from '~constants/listing-management';
import { propertyDraftCategory } from '~constants/pending-approval';
import { generateSlug } from '~helpers';
import { bookingJourneyType } from 'common/constants/booking';
import CancellationBlock from '../cancellation-block';

const FormItem = Form.Item;
const { TextArea } = Input;

@enhanceForm()
export default class PropertyDetail extends React.Component {
  constructor(props) {
    super(props);
    this.mergedProperty = this.mergeDraft();

    this.propertyTenancyAgreementCalculateTypeList = Object.keys(
      propertyTenancyAgreementCalculateType,
    );
    this.propertyTenancyAgreementParams = ['calculateType', 'field', 'timeType', 'unit', 'value'];
  }

  componentDidMount() {
    this.props.onRef(this);
  }

  componentWillUnmount() {
    this.props.onRef(undefined);
  }

  mergeDraft = () => {
    const { property } = this.props;
    // unpublished property always show raw data
    if (
      property &&
      property.drafts &&
      property.drafts.edges.length > 0 &&
      property.status === propertyStatus.PUBLISHED
    ) {
      const propertyDrafts = property.drafts.edges.map(draft => draft.node);
      const detailDraft = propertyDrafts.find(
        draft => draft.category === propertyDraftCategory.DETAIL,
      );

      if (detailDraft) {
        const data = Object.assign({}, property, detailDraft.changes);

        return data;
      }

      return property;
    }

    return property;
  };

  quillCounter = html => {
    const number = getHtmlLength(html);
    return <div className={classNames('property-detail__counter')}>{number}</div>;
  };

  getCityCountry = () => {
    const cityArr = [];

    if (this.mergedProperty.city) {
      cityArr.push(this.mergedProperty.city.name);

      if (this.mergedProperty.city.country) {
        cityArr.push(this.mergedProperty.city.country.name);
      }
    }

    return cityArr.join();
  };

  onNameBlur = e => {
    if (e.target.value && !this.props.form.getFieldValue('slug')) {
      const slug = generateSlug(e.target.value);
      this.props.form.setFields({ slug: { value: slug, touched: true } });
      this.props.form.validateFields(['slug']);
    }
  };

  validateHeadlineCN = (rule, value, callback) => {
    if (!value && !this.props.form.getFieldValue('headline')) {
      callback(this.props.t('cms:cms.properties.edit.detail.headline.blank_message'));
    }
    callback();
  };

  textAreaCounter = (text, limit) => {
    const number = text && text.trim() ? text.trim().length : 0;
    return (
      <div
        className={classNames('property-detail__counter', {
          'property-detail__counter--red': number > limit,
        })}
      >
        {`${number}/${limit}`}
      </div>
    );
  };

  validateHeadlineEN = (rule, value, callback) => {
    if (!value && !this.props.form.getFieldValue('headlineCn')) {
      callback(this.props.t('cms:cms.properties.edit.detail.headline.blank_message'));
    }
    callback();
  };

  validateDescEN = (rule, value, callback) => {
    const isCNFilled =
      this.props.form.getFieldValue('headlineCn') && this.props.form.getFieldValue('descriptionCn');
    if (!value && this.props.form.getFieldValue('headline') && !isCNFilled) {
      callback(this.props.t('cms:cms.listing.modal.error_message.can_not_empty'));
    }
    callback();
  };

  validateDescCN = (rule, value, callback) => {
    const isENFilled =
      this.props.form.getFieldValue('headline') && this.props.form.getFieldValue('description');
    if (!value && this.props.form.getFieldValue('headlineCn') && !isENFilled) {
      callback(this.props.t('cms:cms.listing.modal.error_message.can_not_empty'));
    }
    callback();
  };

  handleSubmit = () =>
    new Promise((resolve, reject) => {
      this.props.form.validateFieldsAndScroll(
        { scroll: { offsetTop: 76, offsetBottom: 76 } },
        err => {
          if (!err) {
            let isUpdate = false;
            let hasTenancyAgreementChanged = false;
            const { changeFields, property } = this.props;
            const propertyTenancyAgreementChangeParams = {};
            // 判断是SEMI_AUTOMATIC还是INSTANT_BOOK
            const isSemiOrInstant =
              this.mergedProperty &&
              (this.mergedProperty.bookingJourney === bookingJourneyType.SEMI_AUTOMATIC ||
                this.mergedProperty.bookingJourney === bookingJourneyType.INSTANT_BOOK);
            if (isSemiOrInstant && !this.mergedProperty.cancellationPeriodType) {
              changeFields.cancellationPeriodType = cancellationPeriodTypeValue.BEFORE_MOVE_IN;
              changeFields.cancellationPeriodValue = 30;
            }

            // Call update/create property tenancy agreement if needed.
            this.propertyTenancyAgreementParams.map(param => {
              const key = `tenancyAgreement${param.charAt(0).toUpperCase() + param.slice(1)}`;
              if (key in changeFields) {
                if (param === 'timeType') {
                  propertyTenancyAgreementChangeParams[param] = changeFields[key]
                    ? propertyTenancyAgreementTimeType.FIXED
                    : propertyTenancyAgreementTimeType.ANYTIME;
                } else {
                  propertyTenancyAgreementChangeParams[param] = changeFields[key];
                }
                delete changeFields[key];
                hasTenancyAgreementChanged = true;
              }
              return true;
            });

            const isCreateAction = !property.tenancyAgreement;
            if (hasTenancyAgreementChanged || isCreateAction) {
              const data = this.buildPropertyTenancyAgreementData(
                propertyTenancyAgreementChangeParams,
                isCreateAction,
              );
              const query = isCreateAction
                ? queries.createPropertyTenancyAgreement(data)
                : queries.updatePropertyTenancyAgreement(data);

              listingManagementFetch(query)
                .then(() => {
                  isUpdate = true;
                })
                .catch(e => {
                  reject(e);
                });
            }

            const changeParams = Object.assign({}, changeFields);
            if (Object.keys(changeParams).length > 0) {
              Object.assign(changeParams, { cancellationChecked: true });

              if (
                'totalBeds' in changeParams &&
                (!changeParams.totalBeds || typeof changeParams.totalBeds !== 'number')
              ) {
                changeParams.totalBeds = 0;
              }

              listingManagementFetch(
                queries.updatePropertyDetails(Object.assign(changeParams, { id: property.id })),
              )
                .then(res => {
                  if (res && res.updatePropertyDetails && res.updatePropertyDetails.property) {
                    Object.assign(changeParams, {
                      drafts: res.updatePropertyDetails.property.drafts,
                    });
                  }

                  isUpdate = true;
                })
                .catch(e => {
                  reject(e);
                });
            }

            if (isUpdate) {
              resolve({ isUpdated: true });
            }
            this.props.afterSave(changeParams);
          } else {
            resolve({ isUpdated: false });
          }
        },
      );
    });

  handleBlurNumberInput = field => {
    if (this.props.form.getFieldValue(field) === null) {
      this.props.form.setFieldsValue({ [field]: 0 });
    }
  };

  onEnTabBlur = () => {
    setTimeout(() => {
      this.props.form.validateFields(['descriptionCn', 'headlineCn'], { force: true });
    }, 100);
  };

  onCnTabBlur = () => {
    setTimeout(() => {
      this.props.form.validateFields(['description', 'headline'], { force: true });
    }, 100);
  };

  getTipsLable = type => (
    <span>
      {this.props.t(`cms:cms.properties.edit.detail.${type}`)}
      <Tooltip title={this.props.t(`cms:cms.properties.edit.detail.${type}.tips`)}>
        <QuestionCircleOutlined className="property-detail__tips-icon" />
      </Tooltip>
    </span>
  );

  buildPropertyTenancyAgreementData = (data, isCreateAction = false) => {
    const params = { ...data };
    const defaultParams = {};

    if (isCreateAction) {
      defaultParams.propertyId = this.props.property.id;
      defaultParams.timeType = propertyTenancyAgreementTimeType.ANYTIME;
      defaultParams.calculateType = propertyTenancyAgreementCalculateType.BEFORE;
      defaultParams.field = propertyTenancyAgreementField.MOVE_IN_DATE;
    } else {
      defaultParams.id = this.mergedProperty.tenancyAgreement.id;
    }

    if (params.timeType === propertyTenancyAgreementTimeType.FIXED) {
      defaultParams.unit = propertyTenancyAgreementUnit.DAY;
      defaultParams.value = 1;
    } else if (params.timeType === propertyTenancyAgreementTimeType.ANYTIME) {
      params.unit = null;
      params.value = null;
    }
    return Object.assign(defaultParams, params);
  };

  render() {
    const {
      t,
      property,
      form: { getFieldDecorator, getFieldValue },
    } = this.props;
    const { Option } = Select;
    // 判断是SEMI_AUTOMATIC还是INSTANT_BOOK
    const isSemiOrInstant =
      this.mergedProperty &&
      (this.mergedProperty.bookingJourney === bookingJourneyType.SEMI_AUTOMATIC ||
        this.mergedProperty.bookingJourney === bookingJourneyType.INSTANT_BOOK);

    return (
      <PropertyDetailWrapper
        t={t}
        isPublished={property.status === propertyStatus.PUBLISHED}
        onClickSave={this.handleSubmit}
        draft={this.props.draft}
        isPendingApprovalSection
        property={property}
      >
        <Form className="property-detail">
          <p className="property-detail__subtitle">
            {t('cms:cms.properties.edit.detail.basic_information')}
          </p>
          <Row>
            <Col span={24}>
              <div className="property-detail__basic-info">
                <span className="property-detail__basic-label">
                  {t('cms:cms.properties.edit.detail.landlord')}
                </span>
                <span className="property-detail__basic-text property-detail__basic-text--ellipsis">
                  {this.mergedProperty.landlord ? this.mergedProperty.landlord.name : null}
                </span>
                <span className="property-detail__line" />
                <span className="property-detail__basic-label">
                  {t('cms:cms.properties.edit.detail.city')}
                  <Tooltip title={this.props.t('cms:cms.properties.edit.detail.city.tips')}>
                    <QuestionCircleOutlined className="property-detail__tips-icon" />
                  </Tooltip>
                </span>
                <span className="property-detail__basic-text">{this.getCityCountry()}</span>
                <span className="property-detail__line" />
                <span className="property-detail__basic-label">
                  {t('cms:cms.properties.edit.detail.currency')}
                </span>
                <span className="property-detail__basic-text">
                  {this.mergedProperty
                    ? `${this.mergedProperty.currency} - ${getSymbolFromCurrency(
                        this.mergedProperty.currency,
                      )}`
                    : '-'}
                </span>
                <span className="property-detail__line" />
                <span className="property-detail__basic-label">
                  {t('cms:cms.properties.edit.detail.billing_cycle')}
                </span>
                <span className="property-detail__basic-text">
                  {this.mergedProperty
                    ? getBillingCycleText(this.mergedProperty.billingCycle, billingCycleOptions)
                    : '-'}
                </span>
                {/* <span className="property-detail__line" />
                <span className="property-detail__basic-label">
                  {t('cms:cms.properties.edit.detail.property_type')}
                </span>
                <span className="property-detail__basic-text">
                  {this.mergedProperty.propertyType ? this.mergedProperty.propertyType.name : null}
                </span>
                <span className="property-detail__line" />
                <span className="property-detail__basic-label">
                  {t('cms:cms.properties.edit.detail.resident_type')}
                </span>
                <span className="property-detail__basic-text">
                  {this.mergedProperty.residentType
                    ? t(
                        `cms:cms.properties.edit.detail.resident_type.${this.mergedProperty.residentType}`,
                      )
                    : null}
                </span> */}
              </div>
            </Col>
          </Row>
          <Row gutter={24} style={{ marginBottom: '8px' }}>
            <Col span={12}>
              <FormItem label={t('cms:cms.properties.edit.detail.property_name')} colon={false}>
                {getFieldDecorator('name', {
                  rules: [{ required: true, message: t('cms:cms.properties.edit.error.blank') }],
                  initialValue: this.mergedProperty.name,
                  validateTrigger: 'onBlur',
                })(
                  <Input
                    placeholder={t('cms:cms.properties.edit.detail.property_name.placeholder')}
                    onBlur={this.onNameBlur}
                  />,
                )}
              </FormItem>
            </Col>
            <Col span={12}>
              <FormItem label={this.getTipsLable('beds_number')} colon={false}>
                {getFieldDecorator('totalBeds', {
                  initialValue: this.mergedProperty.totalBeds,
                  trigger: 'onChange',
                  validateTrigger: 'onBlur',
                })(
                  <InputNumber
                    min={0}
                    placeholder={t('cms:cms.properties.edit.detail.beds_number.placeholder')}
                    precision={0}
                    onBlur={() => this.handleBlurNumberInput('totalBeds')}
                  />,
                )}
              </FormItem>
            </Col>
          </Row>
          <Row gutter={24} style={{ marginBottom: '8px' }}>
            <Col span={12}>
              <FormItem
                label={t('cms:cms.properties.edit.detail.property_type')}
                colon={false}
                required
              >
                <Input
                  value={
                    this.mergedProperty.propertyType ? this.mergedProperty.propertyType.name : null
                  }
                  disabled
                />
              </FormItem>
            </Col>
            <Col span={12}>
              <FormItem
                label={t('cms:cms.properties.edit.detail.resident_type')}
                colon={false}
                required
              >
                <Input
                  value={
                    this.mergedProperty.residentType
                      ? t(
                          `cms:cms.properties.edit.detail.resident_type.${this.mergedProperty.residentType}`,
                        )
                      : null
                  }
                  disabled
                />
              </FormItem>
            </Col>
          </Row>
          <CancellationBlock form={this.props.form} t={t} mergedProperty={this.mergedProperty} />

          <Row gutter={24}>
            <Col span={12}>
              <FormItem label={t('cms:cms.free_cancellation_policy.title')} colon={false}>
                {getFieldDecorator('cancellationProcess', {
                  initialValue: htmlMinify(this.mergedProperty.cancellationProcess),
                  validateTrigger: 'onChange',
                })(
                  <ReactQuill
                    placeholder={t('cms:cms.properties.edit.detail.cancelation_policy.plcaeholder')}
                    fontTypes={[3, false]}
                  />,
                )}
                {this.quillCounter(getFieldValue('cancellationProcess'))}
              </FormItem>
            </Col>
          </Row>

          <If condition={isSemiOrInstant}>
            <p className="property-detail__subtitle">
              {t('cms:cms.properties.edit.detail.tenancy_agreement')}
            </p>
            <Row gutter={24}>
              <Col span={12}>
                <Row gutter={4}>
                  <Col span={24} className="property-detail__tenancy-agreement-time-type-container">
                    <label className="property-detail__tenancy-agreement-time-type-label">
                      {t('cms:cms.properties.edit.detail.tenancy_agreement.label')}
                    </label>
                    <FormItem className="property-detail__tenancy-agreement-time-type">
                      {getFieldDecorator('tenancyAgreementTimeType', {
                        valuePropName: 'checked',
                        initialValue:
                          property.tenancyAgreement &&
                          property.tenancyAgreement.timeType &&
                          property.tenancyAgreement.timeType ===
                            propertyTenancyAgreementTimeType.FIXED,
                      })(
                        <Checkbox className="property-detail__tenancy-agreement-time-label">
                          {t('cms:cms.properties.edit.detail.tenancy_agreement.checkbox.label')}
                        </Checkbox>,
                      )}
                    </FormItem>
                  </Col>
                </Row>
                <Row gutter={8}>
                  <If condition={getFieldValue('tenancyAgreementTimeType')}>
                    <Col span={5} className="property-detail__tenancy-agreement-container">
                      <FormItem>
                        {getFieldDecorator('tenancyAgreementValue', {
                          initialValue:
                            property.tenancyAgreement && property.tenancyAgreement.value
                              ? property.tenancyAgreement.value
                              : 1,
                          rules: [{ required: true, message: ' ' }],
                        })(
                          <InputNumber
                            min={1}
                            max={365}
                            precision={0}
                            className="property-detail__tenancy-agreement-value"
                          />,
                        )}
                      </FormItem>
                    </Col>
                    <Col span={5}>
                      <FormItem>
                        {getFieldDecorator('tenancyAgreementUnit', {
                          validateTrigger: 'onChange',
                          initialValue:
                            property.tenancyAgreement && property.tenancyAgreement.unit
                              ? property.tenancyAgreement.unit
                              : propertyTenancyAgreementUnit.DAY,
                        })(
                          <Select disabled className="property-detail__tenancy-agreement-unit">
                            <Option value={propertyTenancyAgreementUnit.DAY}>
                              {t('cms:cms.properties.edit.detail.tenancy_agreement.unit.day')}
                            </Option>
                          </Select>,
                        )}
                      </FormItem>
                    </Col>
                  </If>
                  <If condition={!getFieldValue('tenancyAgreementTimeType')}>
                    <Col span={10}>
                      <FormItem>
                        <Input
                          disabled
                          value={t(
                            'cms:cms.properties.edit.detail.tenancy_agreement.value.anytime',
                          )}
                        />
                      </FormItem>
                    </Col>
                  </If>
                  <Col span={7}>
                    <FormItem>
                      {getFieldDecorator('tenancyAgreementCalculateType', {
                        validateTrigger: 'onChange',
                        initialValue:
                          property.tenancyAgreement && property.tenancyAgreement.calculateType
                            ? property.tenancyAgreement.calculateType
                            : propertyTenancyAgreementCalculateType.BEFORE,
                      })(
                        <Select className="property-detail__tenancy-agreement-calculate-type">
                          <For of={this.propertyTenancyAgreementCalculateTypeList} each="type">
                            <Option
                              value={type}
                              key={type}
                              className="property-detail__tenancy-agreement-calculate-option"
                            >
                              {t(
                                `cms:cms.properties.edit.detail.tenancy_agreement.calculate_type.option.${type.toLowerCase()}`,
                              )}
                            </Option>
                          </For>
                        </Select>,
                      )}
                    </FormItem>
                  </Col>
                  <Col span={7}>
                    <FormItem>
                      {getFieldDecorator('tenancyAgreementField', {
                        validateTrigger: 'onChange',
                        initialValue:
                          property.tenancyAgreement && property.tenancyAgreement.calculateType
                            ? property.tenancyAgreement.field
                            : propertyTenancyAgreementField.MOVE_IN_DATE,
                      })(
                        <Select disabled className="property-detail__tenancy-agreement-field">
                          <Option value={propertyTenancyAgreementField.MOVE_IN_DATE}>
                            {t(
                              'cms:cms.properties.edit.detail.tenancy_agreement.field.move_in_date',
                            )}
                          </Option>
                        </Select>,
                      )}
                    </FormItem>
                  </Col>
                </Row>
              </Col>
            </Row>
          </If>

          <p className="property-detail__subtitle">
            {t('cms:cms.properties.edit.detail.property_description')}
          </p>
          <Row gutter={24}>
            <Col span={12}>
              <div className="property-detail__description-wrap">
                <FormItem label={this.getTipsLable('headline_cn')} colon={false}>
                  {getFieldDecorator('headlineCn', {
                    initialValue: this.mergedProperty.headlineCn
                      ? this.mergedProperty.headlineCn
                      : undefined,
                    rules: [{ max: 120, message: ' ' }, { validator: this.validateHeadlineCN }],
                    validateTrigger: 'onChange',
                  })(
                    <TextArea
                      style={{ height: '48px' }}
                      placeholder={t('cms:cms.properties.edit.detail.headline.place_holder')}
                      onBlur={this.onCnTabBlur}
                      className="property-detail__headline-textarea"
                    />,
                  )}
                  {this.textAreaCounter(getFieldValue('headlineCn'), 120)}
                </FormItem>
                <FormItem label={t('cms:cms.properties.edit.detail.desc_cn')} colon={false}>
                  {getFieldDecorator('descriptionCn', {
                    rules: [{ validator: this.validateDescCN }],
                    initialValue: htmlMinify(this.mergedProperty.descriptionCn),
                    normalize: this.changeRichText,
                  })(
                    <ReactQuill
                      placeholder={t('cms:cms.properties.edit.detail.desc.placeholder')}
                      onBlur={this.onCnTabBlur}
                    />,
                  )}
                  {this.quillCounter(getFieldValue('descriptionCn'))}
                </FormItem>
              </div>
            </Col>
            <Col span={12}>
              <div className="property-detail__description-wrap">
                <FormItem label={this.getTipsLable('headline_en')} colon={false}>
                  {getFieldDecorator('headline', {
                    initialValue: this.mergedProperty.headline
                      ? this.mergedProperty.headline
                      : undefined,
                    rules: [
                      { max: 300, message: ' ', required: false },
                      { validator: this.validateHeadlineEN },
                    ],
                    validateTrigger: 'onChange',
                  })(
                    <TextArea
                      style={{ height: '48px' }}
                      placeholder={t('cms:cms.properties.edit.detail.headline.place_holder')}
                      onBlur={this.onEnTabBlur}
                      className="property-detail__headline-textarea"
                    />,
                  )}
                  {this.textAreaCounter(getFieldValue('headline'), 300)}
                </FormItem>
                <FormItem label={t('cms:cms.properties.edit.detail.desc_en')} colon={false}>
                  {getFieldDecorator('description', {
                    rules: [{ validator: this.validateDescEN, required: false }],
                    initialValue: htmlMinify(this.mergedProperty.description),
                    normalize: this.changeRichText,
                  })(
                    <ReactQuill
                      placeholder={t('cms:cms.properties.edit.detail.desc.placeholder')}
                      onBlur={this.onEnTabBlur}
                    />,
                  )}
                  {this.quillCounter(getFieldValue('description'))}
                </FormItem>
              </div>
            </Col>
          </Row>
        </Form>
      </PropertyDetailWrapper>
    );
  }
}

PropertyDetail.propTypes = {
  t: PropTypes.func.isRequired,
  property: PropTypes.object,
  form: PropTypes.shape({
    // eslint-disable-line react/require-default-props
    validateFields: PropTypes.func.isRequired,
    getFieldDecorator: PropTypes.func.isRequired,
    getFieldValue: PropTypes.func.isRequired,
    setFields: PropTypes.func.isRequired,
    setFieldsValue: PropTypes.func.isRequired,
    validateFieldsAndScroll: PropTypes.func.isRequired,
  }),
  afterSave: PropTypes.func.isRequired,
  changeFields: PropTypes.object.isRequired,
  onRef: PropTypes.func.isRequired,
  draft: PropTypes.any,
};

PropertyDetail.defaultProps = {
  t: () => {},
  property: {},
  afterSave: () => {},
  changeFields: {},
  onRef: () => {},
  draft: null,
};
